/**
 * Devices Service (Frontend)
 * API calls för enheter
 */

import api from '../api/client';

export interface Device {
  id: number;
  imei: string;
  vehicle_id: string;
  organization_id: string;
  driver_id?: string;
  vehicle_type?: string;
  is_active: boolean;
  vehicle_name?: string;
  model?: string;
  current_firmware?: string;
  current_configuration?: string;
  gsm_number?: string;
  iccid?: string;
  sim_status?: string;
  /** FOTA-status: online/offline från Teltonika (activity_status) */
  fota_online?: boolean;
  /** Senast sedd (FOTA seen_at), ISO-sträng eller Date */
  last_seen?: string | null;
}

export interface SimStatus {
  iccid: string;
  imei: string;
  status: string;
  msisdn?: string;
  ratePlan?: string;
  communicationPlan?: string;
  dateActivated?: string;
  ctdDataUsage?: number;
  ctdSMSUsage?: number;
  ctdSessionCount?: number;
  overageLimitReached?: boolean;
  ipAddress?: string;
  apn?: string;
}

export const devicesService = {
  /**
   * Hämta alla enheter
   */
  getAllDevices: async (searchTerm?: string): Promise<Device[]> => {
    const params: any = {};
    if (searchTerm) params.search = searchTerm;

    const response = await api.get('/api/devices', { params });
    return response.data.data;
  },

  /**
   * Hämta specifik enhet efter IMEI
   */
  getDeviceByImei: async (imei: string): Promise<Device> => {
    const response = await api.get(`/api/devices/${imei}`);
    return response.data.data;
  },

  /**
   * Hämta timestamp för senaste synkronisering
   */
  getLastSyncTimestamp: async (): Promise<string | null> => {
    try {
      const response = await api.get('/api/devices/sync/timestamp');
      return response.data.data?.lastSyncTimestamp || null;
    } catch (error) {
      console.error('Error fetching sync timestamp:', error);
      return null;
    }
  },

  /**
   * Startar nattjobbets enhetssynk (Teltonika/Jasper → Redis/MariaDB) i bakgrunden. Kräver ADMIN_DEVICES (samma som Enheter-sidan).
   */
  triggerFotaDevicesSync: async (): Promise<void> => {
    const response = await api.post('/api/devices/sync/run');
    if (response.status !== 202 || response.data?.success !== true) {
      const msg = response.data?.error || 'Unexpected response';
      throw new Error(msg);
    }
  },

  /**
   * Hämta SIM-status (Jasper) för en enhet via IMEI.
   * Använder GET /api/devices/:imei/sim-status — samma autentisering som övriga enhets-API:er,
   * till skillnad från /api/sim/... som kräver ADMIN_SIM_CARDS.
   */
  getSimStatus: async (imei: string): Promise<SimStatus | null> => {
    try {
      const response = await api.get(`/api/devices/${encodeURIComponent(imei)}/sim-status`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching SIM status:', error);
      return null;
    }
  },

  /**
   * Ändra SIM-status i Jasper för en enhet via IMEI (PUT /api/devices/:imei/sim-status).
   */
  updateSimStatus: async (imei: string, status: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.put(`/api/devices/${encodeURIComponent(imei)}/sim-status`, { status });
      return { success: response.data.success, message: response.data.data?.message };
    } catch (error: any) {
      console.error('Error updating SIM status:', error);
      return { success: false, message: error.response?.data?.error || error.message };
    }
  },
};

